/**
 * Patient-portal session bookkeeping (medopd-docs 107).
 *
 * The patient's access token is NOT stored here any more. It lives only in the HttpOnly `patient_auth_token` cookie
 * the server sets (the requests ask for that with `X-Auth-Transport: cookie`), where no script on the page can read
 * it. It used to be kept — AES-"encrypted" with a key shipped in the bundle — in sessionStorage, readable by any
 * script that ran here.
 *
 * What remains is a non-secret marker that a session was started in this browser, plus the sliding inactivity
 * timeout. The marker only decides whether to ask the server who is signed in (`/patient/auth/me`) on load; the
 * server's answer is the truth. It is in localStorage so a new tab picks up the session the cookie already carries.
 */

const SESSION_KEY = 'patient_session_active';
const SESSION_TIMEOUT_KEY = 'patient_session_timeout';
/** Where the token used to be kept — removed wherever it is still found. */
const LEGACY_TOKEN_KEY = 'patient_auth_token';

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

class PatientStorage {
  constructor() {
    // A token stored by an earlier version of the app must not outlive the upgrade.
    safe(() => sessionStorage.removeItem(LEGACY_TOKEN_KEY), undefined);
  }

  private timeoutMinutes(): number {
    const n = parseInt(import.meta.env.VITE_SESSION_TIMEOUT || '60', 10);
    return Number.isFinite(n) && n > 0 ? n : 60;
  }

  /** Marks that this browser holds a patient session (after sign-in or a successful refresh). */
  markSessionActive(): void {
    safe(() => {
      localStorage.setItem(SESSION_KEY, '1');
      this.touch();
    }, undefined);
  }

  /** Slides the inactivity timeout forward (called on every successful patient request). */
  touch(): void {
    safe(() => {
      if (!localStorage.getItem(SESSION_KEY)) return;
      localStorage.setItem(SESSION_TIMEOUT_KEY, String(Date.now() + this.timeoutMinutes() * 60_000));
    }, undefined);
  }

  /** True while a session was started here and has not been idle past the timeout. */
  hasActiveSession(): boolean {
    return safe(() => {
      if (localStorage.getItem(SESSION_KEY) !== '1') return false;
      const until = parseInt(localStorage.getItem(SESSION_TIMEOUT_KEY) || '0', 10);
      if (!until || Date.now() > until) {
        this.clearSession();
        return false;
      }
      return true;
    }, false);
  }

  /** A session was started here but has been idle past the timeout (the server cookie may still be valid). */
  isIdleExpired(): boolean {
    return safe(() => {
      if (localStorage.getItem(SESSION_KEY) !== '1') return false;
      const until = parseInt(localStorage.getItem(SESSION_TIMEOUT_KEY) || '0', 10);
      return !until || Date.now() > until;
    }, false);
  }

  /** Forgets the session on this browser. The server cookie is ended by /patient/auth/logout. */
  clearSession(): void {
    safe(() => {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SESSION_TIMEOUT_KEY);
      sessionStorage.removeItem(LEGACY_TOKEN_KEY);
      sessionStorage.removeItem(SESSION_TIMEOUT_KEY);
    }, undefined);
  }
}

export const patientStorage = new PatientStorage();
